import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { PortPort } from '../Port/Port';
import type { SlotChipTier, SlotEdge, SlotFace } from '../Slots/Slots';
import type {
  ChassisFan,
  ChassisLayout,
  ChassisPort,
  ChassisPowerSupply,
  ChassisTemperature,
  LayoutColumn,
  LayoutSection,
  PowerSupplyStatus,
} from './types';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { ChassisGap } from '../Chassis/atoms/ChassisGap';
import { ChassisGroup } from '../Chassis/atoms/ChassisGroup';
import { ChassisRear } from '../Chassis/atoms/ChassisRear';
import { ChassisRow } from '../Chassis/atoms/ChassisRow';
import { ChassisSection } from '../Chassis/atoms/ChassisSection';
import { Fan, formatFanSpeed } from '../Fan/Fan';
import { Outlet } from '../Outlet/Outlet';
import { Port } from '../Port/Port';
import { getPortIndicators } from '../Port/utils';
import { SlotEmptyBay } from '../Slots/atoms/SlotEmptyBay';
import { Slot } from '../Slots/Slots';
import {
  describeTemperature,
  formatTemperatureShort,
  getTemperatureHeat,
  getTemperatureLevel,
  Temperature,
} from '../Temperature/Temperature';

/**
 * A neutral box for the disabled tooltip to hang off.
 *
 * `Tooltip` merges its trigger's props into whatever element it wraps, and one of them is an
 * `onClick`. Given straight to the glyph, that reads as "this port is clickable" and the glyph makes
 * itself a button — inside the slot that already is one, which is the second tab stop this file
 * strips `onClick` to avoid. The box takes the merge instead: a `div` with a handler on it is
 * neither focusable nor a widget.
 */
const PortTooltipTarget = styled('div', {
  display: 'flex',
});
PortTooltipTarget.displayName = 'PortTooltipTarget';

const columnKey = (column: LayoutColumn) =>
  `${column.type}-${column.rows.map((row) => row.ports.join(',')).join('|')}`;

const sectionKey = (section: LayoutSection) =>
  `${section.group ?? ''}-${section.columns.map(columnKey).join('|')}`;

const rowEdge = (rowIndex: number): SlotEdge => (rowIndex === 0 ? 'top' : 'bottom');

/**
 * Which lane each port's connection chip hangs in, by port number. See `SlotChip`.
 *
 * Counted across every column rather than per column, so the alternation survives a group boundary —
 * a 12px `trailingGap` is not room for two full-width names side by side. Every declared slot counts
 * whether or not it's patched, so a chip's lane doesn't move when a neighbour is plugged in.
 */
function slotChipTiers(columns: LayoutColumn[]): Map<number, SlotChipTier> {
  const tiers = new Map<number, SlotChipTier>();
  const counts: Record<SlotEdge, number> = { top: 0, bottom: 0 };

  for (const column of columns) {
    for (const [rowIndex, row] of column.rows.entries()) {
      const edge = rowEdge(rowIndex);
      for (const portNumber of row.ports) {
        const count = counts[edge];
        counts[edge] = count + 1;
        tiers.set(portNumber, count % 2 === 0 ? 'near' : 'far');
      }
    }
  }

  return tiers;
}

type ChassisColumnsProps = {
  columns: LayoutColumn[];
  /** Which side of the chassis these columns draw on. `rear` for anything inside a `ChassisRear`. */
  face?: SlotFace;
  portLabels?: Record<number, string>;
  ports: ChassisPort[];
};

function ChassisColumns({ columns, face, portLabels, ports }: ChassisColumnsProps) {
  const tiers = slotChipTiers(columns);

  return columns.map((column, columnIndex) => (
    <Fragment key={`column-${columnKey(column)}`}>
      <ChassisGroup label={column.group} icon={column.groupIcon}>
        {column.rows.map((row, rowIndex) => (
          <ChassisRow key={`row-${column.type}-${row.ports.join('-')}`}>
            {row.ports.map((portNumber) => {
              // A port the caller said nothing about still gets drawn — as an empty socket.
              const port: Partial<ChassisPort> =
                ports.find((candidate) => candidate.number === portNumber) ?? {};
              // The slot owns the click target; a second one on the glyph would double the focus.
              // `poe` and `spanMirroring` belong to the indicators alone — `Port` would put
              // them on the DOM.
              const {
                blockingVariant = 'neutral',
                connection,
                muted,
                onClick,
                poe,
                spanMirroring,
                tooltip,
                ...glyph
              } = port;

              // Named field by field rather than spread, so a new `PortProps` flag can't
              // silently start meaning something to the rail.
              const indicators = getPortIndicators({
                isL3Port: glyph.isL3Port,
                isOspfEnabled: glyph.isOspfEnabled,
                poe,
                spanMirroring,
                uplink: glyph.uplink,
              });

              const diagram = (
                <Port
                  {...glyph}
                  number={portNumber}
                  port={column.type}
                  orientation={row.portOrientation}
                />
              );
              const contents = tooltip ?? (glyph.status === 'disabled' ? 'Disabled' : null);

              return (
                <Slot
                  key={`slot-${column.type}-${portNumber}`}
                  chipSpeed={glyph.negotiatedPortSpeed}
                  chipStatus={glyph.status}
                  chipTier={tiers.get(portNumber)}
                  connection={connection}
                  diagram={
                    contents ? (
                      <Tooltip contents={contents}>
                        <PortTooltipTarget>{diagram}</PortTooltipTarget>
                      </Tooltip>
                    ) : (
                      diagram
                    )
                  }
                  badge={
                    glyph.blocking
                      ? { icon: 'block', label: 'Blocking', variant: blockingVariant }
                      : undefined
                  }
                  edge={rowEdge(rowIndex)}
                  face={face}
                  indicators={indicators}
                  label={portLabels?.[portNumber]}
                  muted={muted}
                  number={portNumber}
                  onClick={onClick}
                  selected={glyph.selected}
                />
              );
            })}
          </ChassisRow>
        ))}
      </ChassisGroup>
      {column.trailingGap && columnIndex < columns.length - 1 && (
        <ChassisGap width={column.trailingGap} />
      )}
    </Fragment>
  ));
}

function ChassisFanSlot({ fan }: { fan: ChassisFan }) {
  const reading = fan.status === 'empty' ? null : fan.speedRpm ?? null;
  const glyph = <Fan status={fan.status} speedRpm={reading ?? undefined} />;

  return (
    <Slot
      face="rear"
      label={fan.label}
      muted={fan.muted}
      diagram={
        reading !== null ? <Tooltip contents={formatFanSpeed(reading)}>{glyph}</Tooltip> : glyph
      }
    />
  );
}

type ChassisBankProps<T> = {
  bays: T[];
  label: string;
  renderBay: (bay: T) => ReactNode;
};

/**
 * A bracketed bank of rear-panel bays, stacked in columns of two like the ports beside them. A lone
 * bay sits on the bottom row, level with a rear panel's OOB port.
 */
function ChassisBank<T>({ bays, label, renderBay }: ChassisBankProps<T>) {
  const top = bays.length > 1 ? bays.filter((_, index) => index % 2 === 0) : [];
  const bottom = bays.length > 1 ? bays.filter((_, index) => index % 2 === 1) : bays;

  return (
    <ChassisGroup label={label}>
      <ChassisRow>{top.map((bay) => renderBay(bay))}</ChassisRow>
      <ChassisRow>{bottom.map((bay) => renderBay(bay))}</ChassisRow>
    </ChassisGroup>
  );
}

export type ChassisPowerSuppliesProps = {
  powerSupplies: ChassisPowerSupply[];
};

const powerSupplyStatusLabels: Record<PowerSupplyStatus, string> = {
  powered: 'Power supply powered',
  unpowered: 'Power supply unpowered',
  failed: 'Power supply failed',
  empty: 'Empty power supply bay',
};

const PowerSupplyEmptyBay = styled(SlotEmptyBay, {
  color: colors.hardwareStrokeNeutralLight,

  [darkThemeSelector]: {
    color: colors.hardwareStrokeNeutralDark,
  },
});
PowerSupplyEmptyBay.displayName = 'PowerSupplyEmptyBay';

function ChassisPowerSupplySlot({ powerSupply }: { powerSupply: ChassisPowerSupply }) {
  const label = powerSupplyStatusLabels[powerSupply.status];

  return (
    <Slot
      face="rear"
      label={powerSupply.label}
      muted={powerSupply.muted}
      diagram={
        <Tooltip contents={label}>
          <PortTooltipTarget role="img" aria-label={label}>
            {powerSupply.status === 'empty' ? (
              <PowerSupplyEmptyBay />
            ) : (
              <Outlet
                connector="iec-c14"
                error={powerSupply.status === 'failed'}
                orientation={powerSupply.orientation === 'down' ? 'bottom' : 'top'}
                status={powerSupply.status === 'powered' ? 'connected' : 'disconnected'}
              />
            )}
          </PortTooltipTarget>
        </Tooltip>
      }
    />
  );
}

export function ChassisPowerSupplies({ powerSupplies }: ChassisPowerSuppliesProps) {
  return (
    <ChassisBank
      bays={powerSupplies}
      label="PSUs"
      renderBay={(powerSupply) => (
        <ChassisPowerSupplySlot key={`psu-${powerSupply.label}`} powerSupply={powerSupply} />
      )}
    />
  );
}

function ChassisTemperatureSlot({ temperature }: { temperature: ChassisTemperature }) {
  const { celsius, muted, name, range, scale } = temperature;
  const heat = getTemperatureHeat(celsius, range);

  return (
    <Slot
      face="rear"
      label={formatTemperatureShort(celsius, scale)}
      muted={muted}
      diagram={
        <Tooltip contents={describeTemperature(celsius, { heat, name, scale })}>
          <Temperature
            celsius={celsius}
            heat={heat}
            level={getTemperatureLevel(celsius, range)}
            muted={muted}
            name={name}
            scale={scale}
          />
        </Tooltip>
      }
    />
  );
}

export type ChassisClimateProps = {
  fans?: ChassisFan[];
  temperatures?: ChassisTemperature[];
};

type ChassisClimateBay =
  | { kind: 'fan'; fan: ChassisFan }
  | { kind: 'temperature'; temperature: ChassisTemperature };

/**
 * Fans then thermal sensors in one bank, so a unit with one of each fills a single column. Fans
 * alone are captioned "Fans".
 */
export function ChassisClimate({ fans = [], temperatures = [] }: ChassisClimateProps) {
  const bays: ChassisClimateBay[] = [
    ...fans.map((fan): ChassisClimateBay => ({ kind: 'fan', fan })),
    ...temperatures.map((temperature): ChassisClimateBay => ({ kind: 'temperature', temperature })),
  ];

  return (
    <ChassisBank
      bays={bays}
      label={temperatures.length > 0 ? 'Climate' : 'Fans'}
      renderBay={(bay) =>
        bay.kind === 'fan' ? (
          <ChassisFanSlot key={`fan-${bay.fan.label}`} fan={bay.fan} />
        ) : (
          <ChassisTemperatureSlot
            key={`temp-${bay.temperature.name}`}
            temperature={bay.temperature}
          />
        )
      }
    />
  );
}

export type ChassisPortsProps = {
  /** Fan bays, drawn in the rear panel's "Climate" bank after any rear ports. */
  fans?: ChassisFan[];
  /** Where this unit's ports sit on its faceplate. */
  layout: ChassisLayout;
  /** The unit's ports, in any order — the layout decides where each number lands. */
  ports: ChassisPort[];
  /** Swappable power supply bays, drawn in the unit's rear panel after the "Climate" bank. */
  powerSupplies?: ChassisPowerSupply[];
  /** Thermal sensor readings, drawn after any fans in the rear panel's "Climate" bank. */
  temperatures?: ChassisTemperature[];
};

/**
 * Draws one unit's faceplate from a model layout: a `ChassisSection` per section, a `ChassisGroup`
 * per column, a `ChassisRow` per row, a `Slot` per port. Meant to sit inside a `ChassisUnit`.
 *
 * Numbers in the layout are the unit's own port numbers, so an HA pair is two units each numbered
 * from 1 rather than one unit with an offset.
 */
export function ChassisPorts({
  fans = [],
  layout,
  ports,
  powerSupplies = [],
  temperatures = [],
}: ChassisPortsProps) {
  const rearSectionIndex = layout.sections.reduce(
    (found, section, index) => (section.rearColumns ? index : found),
    -1,
  );
  const hasBays = fans.length > 0 || powerSupplies.length > 0 || temperatures.length > 0;
  const bays = (
    <>
      {(fans.length > 0 || temperatures.length > 0) && (
        <ChassisClimate fans={fans} temperatures={temperatures} />
      )}
      {powerSupplies.length > 0 && <ChassisPowerSupplies powerSupplies={powerSupplies} />}
    </>
  );

  return (
    <>
      {layout.sections.map((section, sectionIndex) => (
        <Fragment key={`section-${sectionKey(section)}`}>
          <ChassisSection label={section.group} icon={section.groupIcon}>
            <ChassisColumns
              columns={section.columns}
              portLabels={section.portLabels}
              ports={ports}
            />
          </ChassisSection>
          {section.trailingGap && sectionIndex < layout.sections.length - 1 && (
            <ChassisGap width={section.trailingGap} />
          )}
        </Fragment>
      ))}
      {layout.sections.map(
        (section, sectionIndex) =>
          section.rearColumns && (
            <ChassisRear key={`rear-${sectionKey(section)}`}>
              <ChassisColumns
                columns={section.rearColumns}
                face="rear"
                portLabels={section.portLabels}
                ports={ports}
              />
              {sectionIndex === rearSectionIndex && bays}
            </ChassisRear>
          ),
      )}
      {rearSectionIndex === -1 && hasBays && <ChassisRear>{bays}</ChassisRear>}
    </>
  );
}

export type ChassisRearPanelPort = {
  /** Distinguishes this entry's column from the others. The owning unit's id does. */
  id: string;
  /** The owning unit's ports. Each entry brings its own, so two units may both number theirs 0. */
  ports: ChassisPort[];
  /** Which of those ports the panel draws. */
  number: number;
  /** Connector. */
  type: PortPort;
  /** Number-chip text, since these ports are named rather than numbered ('OOB'). */
  label?: string;
};

export type ChassisRearPanelProps = {
  /** One column per port, in faceplate order. */
  ports: ChassisRearPanelPort[];
};

/**
 * A rear panel holding one port from each of several units.
 *
 * A unit whose rear ports are its own declares them as its layout's `rearColumns`. A pair that
 * gathers both halves' management ports into one panel at the far edge of the card can't — the
 * panel outlives either layout — so it passes them here instead, each with the ports of the unit it
 * came from. Render it as the last child of the trailing `ChassisUnit`.
 */
export function ChassisRearPanel({ ports }: ChassisRearPanelProps) {
  return (
    <ChassisRear>
      {ports.map((port) => (
        <ChassisColumns
          key={`rear-panel-${port.id}`}
          face="rear"
          columns={[
            {
              type: port.type,
              rows: [
                // Empty row pads the column so the port sits on the unit's bottom edge, as it does
                // when the panel comes from a layout's own `rearColumns`.
                { ports: [], portOrientation: 'up' },
                { ports: [port.number], portOrientation: 'up' },
              ],
            },
          ]}
          portLabels={port.label !== undefined ? { [port.number]: port.label } : undefined}
          ports={port.ports}
        />
      ))}
    </ChassisRear>
  );
}
